import { z } from "zod";
import {
  DomainModelMetaExtendedSchema,
  DomainModelSchema,
} from "../util/index.ts";

/** Investigation lifecycle values returned by Carbon Hub API. */
export const InvestigationStatus = {
  PENDING: "pending",
  RUNNING: "running",
  DONE: "done",
  ERROR: "error",
} as const;
export type InvestigationStatusType =
  (typeof InvestigationStatus)[keyof typeof InvestigationStatus];

export const TERMINAL_INVESTIGATION_STATUSES = [
  InvestigationStatus.DONE,
  InvestigationStatus.ERROR,
] as const satisfies readonly InvestigationStatusType[];

export const InvestigationMessageRole = {
  USER: "user",
  AGENT: "agent",
} as const;
export type InvestigationMessageRoleType =
  (typeof InvestigationMessageRole)[keyof typeof InvestigationMessageRole];

/** Investigation message categories emitted by Carbon Hub backend. */
export const InvestigationMessageKind = {
  MESSAGE: "message",
  PENDING_APPROVAL: "pending_approval",
  APPROVAL_DECISION: "approval_decision",
} as const;
export type InvestigationMessageKindType =
  (typeof InvestigationMessageKind)[keyof typeof InvestigationMessageKind];

/** Operator decisions accepted when resuming an interrupted investigation turn. */
export const ApprovalDecision = {
  APPROVE: "approve",
  REJECT: "reject",
  ABANDON: "abandon",
} as const;
export type ApprovalDecisionType =
  (typeof ApprovalDecision)[keyof typeof ApprovalDecision];

/** Metadata payload persisted on `approval_decision` messages. */
export const ApprovalDecisionMetadataSchema = z
  .object({
    decision: z.nativeEnum(ApprovalDecision),
  })
  .passthrough();
export type ApprovalDecisionMetadataType = z.infer<
  typeof ApprovalDecisionMetadataSchema
>;

/**
 * Progress event emitted by the investigation agent (NDJSON / stored JSON).
 * `v` and `ts` are wire-protocol names from the agent; see follow-up to rename.
 *
 * `.passthrough()` preserves agent-supplied debug/meta keys at runtime so the UI
 * can surface them in the "Show details" disclosure of the progress feed without
 * us needing to enumerate every possible field here.
 */
export const StepInvestigationProgressEventSchema = z
  .object({
    /** Event protocol version emitted by the investigation agent. */
    v: z.literal(1),
    type: z.literal("step"),
    /** ISO-8601 event timestamp from the agent. */
    ts: z.string().datetime(),
    phase: z.string().min(1),
    detail: z.string(),
  })
  .passthrough();
export type StepInvestigationProgressEventType = z.infer<
  typeof StepInvestigationProgressEventSchema
> &
  Record<string, unknown>;

export const InvestigationProgressEventSchema = z.discriminatedUnion("type", [
  StepInvestigationProgressEventSchema,
]);
export type InvestigationProgressEventType = z.infer<
  typeof InvestigationProgressEventSchema
>;

export const InvestigationErrorSchema = z
  .object({
    message: z.string().min(1),
  })
  .passthrough();
export type InvestigationErrorType = z.infer<typeof InvestigationErrorSchema>;

/** Human-review summary of the spec proposed for registration. */
export const SpecSummarySchema = z
  .object({
    title: z.string().min(1),
    version: z.string().min(1),
    openapi_version: z.string().min(1),
  })
  .passthrough();
export type SpecSummaryType = z.infer<typeof SpecSummarySchema>;

/**
 * Agent payload for `pending_approval` messages.
 * Mirrors the passthrough shape persisted by Carbon Hub worker tests.
 */
export const PendingApprovalMetadataSchema = z
  .object({
    key: z.string().min(1),
    spec_summary: SpecSummarySchema,
    hosts: z.array(z.string()),
    post_read_suffixes: z.array(z.string()).optional(),
  })
  .passthrough();
export type PendingApprovalMetadataType = z.infer<
  typeof PendingApprovalMetadataSchema
>;

/** Reference to a DP file that has been uploaded via the file API. */
export const DpFileArtifactSchema = z
  .object({
    kind: z.literal("dp_file"),
    name: z.string().min(1),
    mime_type: z.string().min(1),
    ref: z
      .object({
        file_id: z.string().uuid(),
      })
      .strict(),
  })
  .strict();

/** Artifact union for investigation-message metadata payloads. */
export const ArtifactRefSchema = z.discriminatedUnion("kind", [
  DpFileArtifactSchema,
]);
export type ArtifactRefType = z.infer<typeof ArtifactRefSchema>;

export const InvestigationMessageSchema = z
  .object({
    investigation_id: z.string().uuid(),
    role: z.nativeEnum(InvestigationMessageRole),
    kind: z.nativeEnum(InvestigationMessageKind),
    content: z.string(),
    metadata: z.record(z.unknown()).nullable(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);
export type InvestigationMessageType = z.infer<
  typeof InvestigationMessageSchema
>;

export const InvestigationSchema = z
  .object({
    company_id: z.string().uuid(),
    user_id: z.string().uuid(),
    status: z.nativeEnum(InvestigationStatus),
    runtime_session_id: z.string().min(33),
    progress: z.array(InvestigationProgressEventSchema),
    report: z.string().nullable(),
    error: InvestigationErrorSchema.nullable(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);
export type InvestigationType = z.infer<typeof InvestigationSchema>;

/** Detail view shape: API GET may omit `messages` until conversation UI ships (F2). */
export type InvestigationDetailType = InvestigationType & {
  messages: InvestigationMessageType[];
};

/**
 * Maximum length, in characters, of the `content` field on an investigation
 * prompt or follow-up message. Mirrored in `CreateInvestigation*BodySchema`
 * below so the API contract and the form-level validation can't drift.
 */
export const MAX_INVESTIGATION_MESSAGE_LENGTH = 10_000;

export const CreateInvestigationBodySchema = z.object({
  content: z.string().min(1).max(MAX_INVESTIGATION_MESSAGE_LENGTH),
});
export type CreateInvestigationBodyType = z.infer<
  typeof CreateInvestigationBodySchema
>;

export const CreateInvestigationResponseSchema = z.object({
  investigation_id: z.string().uuid(),
});
export type CreateInvestigationResponseType = z.infer<
  typeof CreateInvestigationResponseSchema
>;

export const CreateInvestigationMessageBodySchema = z.discriminatedUnion(
  "kind",
  [
    z
      .object({
        kind: z.literal("message"),
        content: z.string().min(1).max(MAX_INVESTIGATION_MESSAGE_LENGTH),
        metadata: z
          .object({
            artifacts: z.array(ArtifactRefSchema),
          })
          .strict()
          .optional(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("approval"),
        decision: z.nativeEnum(ApprovalDecision),
        content: z
          .string()
          .min(1)
          .max(MAX_INVESTIGATION_MESSAGE_LENGTH)
          .optional(),
      })
      .strict(),
  ]
);
export type CreateInvestigationMessageBodyType = z.infer<
  typeof CreateInvestigationMessageBodySchema
>;

export const CreateInvestigationMessageResponseSchema = z.object({
  investigation_id: z.string().uuid(),
});
export type CreateInvestigationMessageResponseType = z.infer<
  typeof CreateInvestigationMessageResponseSchema
>;

export const ListInvestigationsResponseSchema = z.object({
  page_size: z.number().min(0),
  page_number: z.number().min(1),
  total_entries: z.number().min(0),
  total_pages: z.number().min(0),
  data: z.array(InvestigationSchema),
});
export type ListInvestigationsResponseType = z.infer<
  typeof ListInvestigationsResponseSchema
>;

export const ListInvestigationMessagesResponseSchema = z.object({
  page_size: z.number().min(0),
  page_number: z.number().min(1),
  total_entries: z.number().min(0),
  total_pages: z.number().min(0),
  data: z.array(InvestigationMessageSchema),
});
export type ListInvestigationMessagesResponseType = z.infer<
  typeof ListInvestigationMessagesResponseSchema
>;
